import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatCurrency } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  presupuesto: { id: string; saldoPendiente: number; proveedorId: string };
};

const empty = { monto: '', metodo: 'transferencia', referencia: '', notas: '' };

export function PagoProveedorDialog({ open, onOpenChange, presupuesto }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);

  const f = (field: keyof typeof empty) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(p => ({ ...p, [field]: e.target.value }));

  const mut = useMutation({
    mutationFn: (data: any) =>
      api.post(`/api/presupuestos-proveedor/${presupuesto.id}/pagos`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presupuesto-proveedor-detail', presupuesto.id] });
      qc.invalidateQueries({ queryKey: ['presupuestos-proveedor', presupuesto.proveedorId] });
      qc.invalidateQueries({ queryKey: ['presupuestos-proveedor-all'] });
      toast.success('Pago registrado');
      onOpenChange(false);
      setForm(empty);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar el pago')),
  });

  const saldo = Number(presupuesto.saldoPendiente ?? 0);

  return (
    <Dialog.Root open={open} onOpenChange={v => { onOpenChange(v); if (!v) setForm(empty); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-md p-6 focus:outline-none">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-text-primary">Registrar pago</Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </Dialog.Close>
          </div>

          {saldo > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-700">Saldo pendiente</p>
              <p className="text-xl font-bold text-amber-800">{formatCurrency(saldo)}</p>
            </div>
          )}

          <form
            onSubmit={e => {
              e.preventDefault();
              mut.mutate({ ...form, monto: parseFloat(form.monto) });
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Monto <span className="text-red-500">*</span></label>
              <input
                required type="number" min="0.01" step="0.01"
                value={form.monto} onChange={f('monto')}
                placeholder={saldo > 0 ? String(saldo.toFixed(2)) : '0.00'}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Método <span className="text-red-500">*</span></label>
              <select required value={form.metodo} onChange={f('metodo')}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Referencia / Número de transacción</label>
              <input value={form.referencia} onChange={f('referencia')} placeholder="Folio, número de cheque, etc."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Notas</label>
              <textarea rows={2} value={form.notas} onChange={f('notas')} placeholder="Notas adicionales..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
            </div>

            <div className="flex gap-3 pt-2">
              <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                Cancelar
              </Dialog.Close>
              <button type="submit" disabled={mut.isPending}
                className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                {mut.isPending ? 'Guardando...' : 'Registrar pago'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
